import type { FC, ReactNode } from 'react'
import type { Arena } from '@/constants/landing'

import { useEffect, useState } from 'react'
import { CalendarClock, Radio, Trophy } from 'lucide-react'
import { cn } from '@/lib/utils'
import NeonLink from '@/components/landing/neon-link'
import SectionHeading from '@/components/landing/section-heading'
import { ARENAS } from '@/constants/landing'

interface Occurrence {
    arena: Arena
    start: Date
    end: Date
    live: boolean
}

const nextOccurrence = (arena: Arena, now: Date): Occurrence => {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), arena.hourUtc))
    start.setUTCDate(start.getUTCDate() + ((arena.weekday - now.getUTCDay() + 7) % 7))

    let end = new Date(start.getTime() + arena.durationMinutes * 60_000)

    if (end.getTime() <= now.getTime()) {
        start.setUTCDate(start.getUTCDate() + 7)
        end = new Date(start.getTime() + arena.durationMinutes * 60_000)
    }

    return {
        arena,
        start,
        end,
        live: start.getTime() <= now.getTime() && now.getTime() < end.getTime()
    }
}

const pad = (value: number): string => String(value).padStart(2, '0')

const ArenasSection: FC = (): ReactNode => {
    const [now, setNow] = useState<Date | null>(null)

    useEffect(() => {
        setNow(new Date())
        const timer = window.setInterval(() => setNow(new Date()), 1000)
        return () => window.clearInterval(timer)
    }, [])

    const occurrences = now ? ARENAS.map(arena => nextOccurrence(arena, now)) : []
    const live = occurrences.find(occurrence => occurrence.live) ?? null
    const upcoming = occurrences.length
        ? [...occurrences].filter(occurrence => !occurrence.live).sort((first, second) => first.start.getTime() - second.start.getTime())[0]
        : null

    const target = live ?? upcoming
    const remaining = target && now ? Math.max(0, (live ? target.end : target.start).getTime() - now.getTime()) : null

    const units = remaining === null
        ? [['--', 'Days'], ['--', 'Hours'], ['--', 'Minutes'], ['--', 'Seconds']]
        : [
            [pad(Math.floor(remaining / 86_400_000)), 'Days'],
            [pad(Math.floor(remaining / 3_600_000) % 24), 'Hours'],
            [pad(Math.floor(remaining / 60_000) % 60), 'Minutes'],
            [pad(Math.floor(remaining / 1000) % 60), 'Seconds']
        ]

    const localTime = (arena: Arena): string | null => {
        const occurrence = occurrences.find(item => item.arena.name === arena.name)
        if (!occurrence) return null

        return new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' }).format(occurrence.start)
    }

    return (
        <section id='arenas' aria-labelledby='arenas-title' className='relative scroll-mt-20 py-24 sm:py-32'>
            <div className='mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:gap-16'>
                <div>
                    <SectionHeading
                        id='arenas-title'
                        eyebrow='Nightly arenas'
                        accent='amber'
                        title='Every night is tournament night.'
                        description="Arenas run on a fixed weekly schedule. Join any time while the clock is running, play as many games as you can and watch the live leaderboard reshuffle after every result."
                    />

                    <div className='mt-10 rounded-3xl border border-neon-amber/30 bg-neon-amber/5 p-6 shadow-[0_0_40px_rgb(255_181_71/0.08)] sm:p-7'>
                        <p className='flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.22em] text-neon-amber'>
                            {live ? <Radio className='size-4' /> : <CalendarClock className='size-4' />}
                            {live ? 'Live now · ends in' : 'Next arena starts in'}
                        </p>

                        <p className='mt-3 font-heading text-xl font-semibold tracking-tight'>
                            {target ? target.arena.name : 'Loading the schedule'}
                        </p>

                        <div className='mt-6 grid grid-cols-4 gap-2 sm:gap-3' role='timer' aria-live='off'>
                            {units.map(([value, label]) => (
                                <div key={label} className='rounded-2xl border border-white/10 bg-black/40 px-2 py-3 text-center'>
                                    <p className='font-mono text-2xl font-semibold tabular-nums text-neon-amber sm:text-3xl' style={{ textShadow: '0 0 16px rgb(255 181 71 / 0.55)' }}>
                                        {value}
                                    </p>

                                    <p className='mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground'>
                                        {label}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <NeonLink href='#play' size='sm' variant='outline' className='mt-6'>
                            Warm up with a game
                        </NeonLink>
                    </div>
                </div>

                <ul className='flex flex-col gap-4 lg:pt-4'>
                    {ARENAS.map(arena => {
                        const local = localTime(arena)
                        const isLive = live?.arena.name === arena.name

                        return (
                            <li
                                key={arena.name}
                                className={cn(
                                    'relative flex gap-5 rounded-3xl border p-5 transition-colors sm:p-6',
                                    arena.featured ? 'border-neon-pink/45 bg-neon-pink/5 shadow-[0_0_36px_rgb(255_61_206/0.12)]' : 'border-white/10 bg-card/50 hover:border-white/20'
                                )}
                            >
                                <div className='flex w-16 shrink-0 flex-col items-center justify-center rounded-2xl border border-white/10 bg-black/40 py-3 sm:w-20'>
                                    <p className='font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground'>
                                        {arena.day.slice(0, 3)}
                                    </p>

                                    <p className='mt-1 font-heading text-lg font-semibold sm:text-xl'>
                                        {pad(arena.hourUtc)}:00
                                    </p>

                                    <p className='font-mono text-[10px] text-muted-foreground'>
                                        UTC
                                    </p>
                                </div>

                                <div className='min-w-0 flex-1'>
                                    <div className='flex flex-wrap items-center gap-2'>
                                        <h3 className='font-heading text-base font-semibold tracking-tight sm:text-lg'>
                                            {arena.name}
                                        </h3>

                                        {arena.featured && (
                                            <span className='inline-flex items-center gap-1 rounded-full border border-neon-pink/45 bg-neon-pink/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-neon-pink'>
                                                <Trophy className='size-3' />
                                                Flagship
                                            </span>
                                        )}

                                        {isLive && (
                                            <span className='inline-flex items-center gap-1.5 rounded-full border border-neon-lime/45 bg-neon-lime/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-neon-lime'>
                                                <span className='size-1.5 rounded-full bg-neon-lime' />
                                                Live
                                            </span>
                                        )}
                                    </div>

                                    <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                                        {arena.description}
                                    </p>

                                    <p className='mt-3 flex flex-wrap gap-x-3 gap-y-1 font-mono text-xs text-foreground/70'>
                                        <span>{arena.clock}</span>
                                        <span aria-hidden='true' className='text-white/20'>/</span>
                                        <span>{arena.durationMinutes >= 120 ? `${arena.durationMinutes / 60} hours` : `${arena.durationMinutes} min`}</span>

                                        {local && (
                                            <>
                                                <span aria-hidden='true' className='text-white/20'>/</span>
                                                <span className='text-neon-cyan'>{local} your time</span>
                                            </>
                                        )}
                                    </p>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}

export default ArenasSection
